/**
 * LogsModal — 640px. Stats row + Executions / Change-history tabs.
 * Executions are deterministically synthesized (logsFor). Audit comes from
 * recorded actions.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Close } from '../icons';
import { useBridge } from '../store/bridgeStore';
import { allInts } from '../domain/catalog';
import { versionsFor } from '../domain/versions';
import { logsFor } from '../domain/logs';
// FE↔BE integration (Round 4): adapters that turn backend observability rows
// into the modal's expected shapes.
import { toExecRows, toAuditRows } from '../api/observability';
import { USE_BACKEND } from '../api/config';

const GRID = '64px 1fr 92px 70px 96px';

// Format an ISO timestamp into the modal's compact "Mon D, h:mm AM" style,
// matching the session stamp() format used elsewhere. Falls back to the raw
// string if it isn't parseable.
function formatTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function LogsModal() {
  const { state, actions } = useBridge();
  if (!state.logsId) return null;
  const ints = allInts(state.adapters, state.metaOverrides);
  const intg = ints.find(i => i.id === state.logsId);
  const vs = versionsFor(state.logsId, state.versions, state.adapters);
  // Never synthesize when the backend is the source of truth.
  let L = logsFor(state.logsId, intg, vs, !USE_BACKEND);
  const isExec = state.logsTab === 'exec';

  // Real backend observability replaces the synthesized figures entirely.
  //
  // The rows used to fall back to synthesized ones when the backend returned
  // none, "so the demo view isn't blank". In an ops tool that is a lie with the
  // same styling as the truth: a connector that has never run showed invented
  // executions, latencies and error counts, and someone would debug against
  // traffic that never happened. A never-run connector now renders the honest
  // empty state instead.
  const obs = state.obs[state.logsId];
  if (obs) {
    L = {
      ...L,
      total: obs.stats.total,
      successRate: Math.round(obs.stats.success_rate_24h * 1000) / 10,
      errToday: obs.stats.error_24h,
      rows: toExecRows(obs.executions, Date.now()) as typeof L.rows,
    };
  }

  // Audit: prefer real backend audit when loaded; otherwise the session-recorded
  // entries. Backend timestamps are ISO, so format them; session ones are
  // already display strings.
  const audit = obs
    ? toAuditRows(obs.audit).map(e => ({
        ...e,
        time: formatTime(e.time),
      }))
    : (state.audit[state.logsId] || []).map(e => ({
        ...e,
        tone: e.kind === 'publish' ? 'accent' : e.kind === 'auth' ? 'warn' : 'info',
      }));

  return (
    <View style={styles.overlayHost}>
      {React.createElement(
        'div',
        { className: 't-anim-fadein', onClick: actions.closeLogs, style: { position: 'fixed', inset: 0, background: colors.overlay, display: 'grid', placeItems: 'center', zIndex: 70, padding: 24 } },
        React.createElement(
          'div',
          { className: 't-anim-slideup', onClick: (e: React.MouseEvent) => e.stopPropagation(), style: { background: colors.card, borderRadius: 12, boxShadow: 'var(--shadow-lg)', width: 640, maxWidth: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } },
          <Inner key="i" L={L} intg={intg} isExec={isExec} audit={audit} state={state} actions={actions} />,
        ),
      )}
    </View>
  );
}

function Inner({ L, intg, isExec, audit, state, actions }: any) {
  return (
    <>
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: (intg && intg.color) || '#2D5CF6' } as any]}><Text style={styles.avatarText}>{intg && intg.initials}</Text></View>
        <View style={{ flex: 1 }}>
          <Text style={styles.name}>{(intg && intg.name) + ' · activity'}</Text>
          <Text className="t-mono" style={styles.sub}>{'live v1 × ' + L.live.vendor + ' · rev ' + (L.live.rev || 1)}</Text>
        </View>
        <Pressable onPress={actions.closeLogs}><Close size={18} color={colors.ink3} /></Pressable>
      </View>

      {/* stats */}
      <View style={styles.statsRow}>
        <Stat big={(L.total || 0).toLocaleString()} label="total executions" />
        <Stat big={L.successRate + '%'} label="success (24h)" color={colors.success} />
        <Stat big={String(L.errToday)} label="errors (24h)" color={L.errToday > 0 ? colors.danger : colors.ink} />
        <Stat big={String(L.live.rev || 1)} label="internal rev" />
      </View>

      {/* tabs */}
      <View style={styles.tabs}>
        <Tab label="Executions" active={isExec} onPress={() => actions.setLogsTab('exec')} />
        <Tab label="Change history" active={!isExec} onPress={() => actions.setLogsTab('audit')} />
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 14, paddingHorizontal: 20 }}>
        {isExec && L.rows.length === 0 ? (
          <Text style={styles.auditEmpty}>
            No executions recorded for this connector. Runs appear here once it is connected, granted a data
            type, and actually called.
          </Text>
        ) : isExec ? (
          <View style={{ gap: 5 }}>
            {L.rows.map((r: any) =>
              React.createElement(
                'div',
                { key: r.id, style: { display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center', padding: '9px 12px', border: `1px solid ${colors.line}`, borderLeft: `3px solid var(--${r.statusTone})`, borderRadius: 5, background: colors.paper2 } },
                <View style={styles.execStatus} key="s"><View style={[styles.execDot, { backgroundColor: `var(--${r.statusTone})` } as any]} /><Text style={[styles.execStatusText, { color: `var(--${r.statusTone})` } as any]}>{r.statusLabel}</Text></View>,
                <Text className="t-mono" style={styles.execOp} key="o" numberOfLines={1}>{r.op}</Text>,
                <Text style={styles.execDetail} key="d">{r.detail}</Text>,
                <Text className="t-mono" style={styles.execLat} key="l">{r.latency}</Text>,
                <Text style={styles.execAgo} key="a">{r.ago}</Text>,
              ),
            )}
          </View>
        ) : audit.length > 0 ? (
          <View>
            {audit.map((e: any, i: number) => (
              <View key={i} style={styles.auditRow}>
                <View style={[styles.auditIcon, { backgroundColor: `var(--${e.tone}-soft)` } as any]}><View style={[styles.auditDot, { backgroundColor: `var(--${e.tone})` } as any]} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.auditMsg}>{e.msg}</Text>
                  <Text style={styles.auditMeta}>{e.time + ' · ' + e.actor}</Text>
                </View>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.auditEmpty}>No config changes recorded yet this session. Edits, approvals, and re-auth events show up here.</Text>
        )}
      </ScrollView>
    </>
  );
}

function Stat({ big, label, color = colors.ink }: { big: string; label: string; color?: string }) {
  return (
    <View style={{ flex: 1 }}>
      <Text style={[styles.statBig, { color } as any]}>{big}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}
function Tab({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.tab, active ? styles.tabActive : null]}>
      <Text style={[styles.tabText, active ? { color: colors.ink } as any : null]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  overlayHost: {},
  header: { padding: 18, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 12 },
  avatar: { width: 38, height: 38, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, color: '#fff' },
  name: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  sub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  statsRow: { padding: 16, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', gap: 12 },
  statBig: { fontFamily: fontFamily.sans, fontSize: fontSize.xxl, fontWeight: fontWeight.semibold },
  statLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 1 },
  tabs: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: colors.line, paddingHorizontal: 12 },
  tab: { paddingVertical: 9, paddingHorizontal: 14, borderBottomWidth: 2, borderBottomColor: 'transparent', marginBottom: -1, cursor: 'pointer' } as any,
  tabActive: { borderBottomColor: colors.ink },
  tabText: { fontFamily: fontFamily.sans, fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.ink3 },
  execStatus: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  execDot: { width: 6, height: 6, borderRadius: 3 },
  execStatusText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium, textTransform: 'uppercase', letterSpacing: 0.3 },
  execOp: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink },
  execDetail: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  execLat: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3, textAlign: 'right' },
  execAgo: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, textAlign: 'right' },
  auditRow: { flexDirection: 'row', gap: 12, paddingVertical: 11, paddingHorizontal: 4, borderBottomWidth: 1, borderBottomColor: colors.line },
  auditIcon: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
  auditDot: { width: 7, height: 7, borderRadius: 4 },
  auditMsg: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium },
  auditMeta: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 1 },
  auditEmpty: { textAlign: 'center', paddingVertical: 32, color: colors.ink3, fontFamily: fontFamily.sans, fontSize: fontSize.sm },
});
